import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { PageContainer } from '../components/layout/PageContainer';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoading } from '../components/ui/PageLoading';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FormInput } from '../components/ui/FormInput';
import axios from 'axios';
import toast from 'react-hot-toast';
import { User, Key, Eye, EyeOff, Shield, Mail, Settings as SettingsIcon } from 'lucide-react';

const UserSettings = () => {
  const { user, isRememberedSession } = useAuth();
  const { language } = useLanguage();
  const { basePath } = useCompetition();
  const navigate = useNavigate();

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [loading, setLoading] = useState(false);

  if (!user) {
    return (
      <PageLoading
        message={language === 'en' ? 'Loading settings…' : 'טוען הגדרות…'}
      />
    );
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordForm.currentPassword.trim()) {
      toast.error(language === 'en' ? 'Current password is required' : 'סיסמה נוכחית נדרשת');
      return;
    }
    if (!passwordForm.newPassword.trim()) {
      toast.error(language === 'en' ? 'New password is required' : 'סיסמה חדשה נדרשת');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(
        language === 'en' ? 'New passwords do not match' : 'הסיסמאות החדשות לא תואמות'
      );
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error(
        language === 'en'
          ? 'New password must be at least 6 characters'
          : 'הסיסמה החדשה חייבת להכיל לפחות 6 תווים'
      );
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error(
        language === 'en'
          ? 'New password must be different from current password'
          : 'הסיסמה החדשה חייבת להיות שונה מהסיסמה הנוכחית'
      );
      return;
    }

    try {
      setLoading(true);
      await axios.put('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      toast.success(language === 'en' ? 'Password changed successfully!' : 'הסיסמה שונתה בהצלחה!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (language === 'en' ? 'Failed to change password' : 'נכשל בשינוי הסיסמה');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const passwordToggle = (field) => (
    <button
      type="button"
      onClick={() => togglePasswordVisibility(field)}
      className="text-zinc-500 hover:text-zinc-700"
      tabIndex={-1}
    >
      {showPasswords[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  const displayName = user.fullName || user.username || 'User';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <PageContainer>
      <PageHeader
        title={language === 'en' ? 'Account settings' : 'הגדרות חשבון'}
        subtitle={
          language === 'en' ? 'Manage your account preferences' : 'נהל את העדפות החשבון שלך'
        }
        backTo={`${basePath}/dashboard`}
        icon={SettingsIcon}
      />

      <div className="mx-auto max-w-xl space-y-6">
        <Card>
          <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-zinc-900">
            <User className="h-5 w-5 text-brand-600" />
            {language === 'en' ? 'Profile information' : 'פרטי פרופיל'}
          </h2>
          <div className="flex items-center gap-4">
            <div
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white ${
                user.isAdmin
                  ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                  : 'bg-gradient-to-br from-brand-500 to-brand-700'
              }`}
            >
              {initial}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-semibold text-zinc-900">{displayName}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-zinc-600">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="truncate">{user.email}</span>
              </p>
              {user.isAdmin ? (
                <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                  <Shield className="h-3 w-3" />
                  {language === 'en' ? 'Administrator' : 'מנהל'}
                </span>
              ) : null}
            </div>
          </div>
          {isRememberedSession() ? (
            <p className="mt-4 rounded-lg border border-brand-100 bg-brand-50 px-3 py-2 text-sm text-brand-900">
              {language === 'en'
                ? 'You are logged in with an extended 30-day session.'
                : 'אתה מחובר עם הפעלה מורחבת של 30 יום.'}
            </p>
          ) : null}
        </Card>

        <Card>
          <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-zinc-900">
            <Key className="h-5 w-5 text-brand-600" />
            {language === 'en' ? 'Change password' : 'שנה סיסמה'}
          </h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <FormInput
              id="currentPassword"
              type={showPasswords.current ? 'text' : 'password'}
              label={language === 'en' ? 'Current password' : 'סיסמה נוכחית'}
              value={passwordForm.currentPassword}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
              }
              required
              disabled={loading}
              dir="ltr"
              suffix={passwordToggle('current')}
            />
            <FormInput
              id="newPassword"
              type={showPasswords.new ? 'text' : 'password'}
              label={language === 'en' ? 'New password' : 'סיסמה חדשה'}
              value={passwordForm.newPassword}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, newPassword: e.target.value })
              }
              required
              minLength={6}
              disabled={loading}
              dir="ltr"
              suffix={passwordToggle('new')}
            />
            <p className="-mt-2 text-xs text-zinc-500">
              {language === 'en' ? 'Must be at least 6 characters' : 'חייב להכיל לפחות 6 תווים'}
            </p>
            <FormInput
              id="confirmPassword"
              type={showPasswords.confirm ? 'text' : 'password'}
              label={language === 'en' ? 'Confirm new password' : 'אשר סיסמה חדשה'}
              value={passwordForm.confirmPassword}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
              }
              required
              minLength={6}
              disabled={loading}
              dir="ltr"
              suffix={passwordToggle('confirm')}
            />
            <Button type="submit" loading={loading} className="w-full sm:w-auto">
              <Key className="h-4 w-4" />
              {language === 'en' ? 'Change password' : 'שנה סיסמה'}
            </Button>
          </form>
        </Card>

        <Card className="border-brand-100 bg-brand-50/50" padding="p-4">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-900">
            <SettingsIcon className="h-4 w-4" />
            {language === 'en' ? 'Security tips' : 'טיפים לאבטחה'}
          </h3>
          <ul className="list-inside list-disc space-y-1 text-sm text-brand-900/90">
            <li>
              {language === 'en'
                ? 'Use a strong password with letters, numbers, and symbols'
                : 'השתמש בסיסמה חזקה עם אותיות, מספרים וסמלים'}
            </li>
            <li>
              {language === 'en' ? "Don't share your password" : 'אל תשתף את הסיסמה שלך'}
            </li>
            <li>
              {language === 'en'
                ? 'Change your password regularly'
                : 'שנה את הסיסמה באופן קבוע'}
            </li>
          </ul>
        </Card>

        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => navigate(`${basePath}/dashboard`)}>
            {language === 'en' ? 'Back to dashboard' : 'חזור ללוח הבקרה'}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
};

export default UserSettings;
